import { Loader2 } from 'lucide-react'
import type { PresenceViewerDto, QuestionThreadDto } from '@lib/dto/question.dto'
import type { QuestionRole } from '@lib/enums/question-role.enum'
import { ENVELOPE_LIMITS } from '@lib/constants/envelope'
import { Button } from '@/components/ui/button'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { Field } from '@/components/ui/field'
import { Textarea } from '@/components/ui/textarea'
import type { Uuid } from '@lib/validators/params'

export type DraftPin = {
  documentId: Uuid
  page: number
  x: number
  y: number
  quote: string | null
}

export type QuestionLayer = {
  threads: readonly QuestionThreadDto[]
  draft: DraftPin | null
  selectedThreadId: string | null
  onSelectThread: (id: string) => void
  onAskPin?: ((pin: DraftPin) => void) | undefined
}

export function QuestionSidebar({
  role,
  threads,
  selectedThreadId,
  draft,
  composer,
  pending,
  onComposerChange,
  onAsk,
  onReply,
  onCancelDraft,
  onSelectThread,
  viewers = [],
}: {
  role: QuestionRole
  threads: readonly QuestionThreadDto[]
  selectedThreadId: string | null
  draft: DraftPin | null
  composer: string
  pending: boolean
  onComposerChange: (value: string) => void
  onAsk?: () => void
  onReply: () => void
  onCancelDraft: () => void
  onSelectThread: (id: string) => void
  viewers?: readonly PresenceViewerDto[]
}) {
  const selected = threads.find((thread) => thread.id === selectedThreadId) ?? null
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">Questions</CardTitle>
        <CardDescription>
          {role === 'asker'
            ? 'Click the document to ask. Replies show up here.'
            : 'Reply to questions from the signer.'}
          {viewers.length > 0
            ? ` Also here: ${viewers.map((viewer) => viewer.name).join(', ')}.`
            : ''}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {threads.length === 0 && !draft ? (
          <p className="text-sm text-muted-foreground">
            {role === 'asker'
              ? 'Click the document to ask a question.'
              : 'No questions yet.'}
          </p>
        ) : (
          <ol className="max-h-48 space-y-1 overflow-y-auto text-sm">
            {threads.map((thread, index) => (
              <li key={thread.id}>
                <button
                  type="button"
                  className={`w-full rounded-md px-2 py-1.5 text-left hover:bg-muted ${thread.id === selectedThreadId ? 'bg-muted' : ''}`}
                  onClick={() => onSelectThread(thread.id)}
                >
                  {index + 1}. {thread.quote ?? 'Question'}
                </button>
              </li>
            ))}
          </ol>
        )}
        {draft && role === 'asker' ? (
          <div className="space-y-2 border-t border-border pt-4">
            {draft.quote ? (
              <p className="text-xs text-muted-foreground">Near: {draft.quote}</p>
            ) : null}
            <Field label="Question" htmlFor="question-body">
              <Textarea
                id="question-body"
                value={composer}
                onChange={(event) => onComposerChange(event.target.value)}
                rows={4}
                maxLength={ENVELOPE_LIMITS.maxQuestionChars}
              />
            </Field>
            <div className="flex gap-2">
              <Button disabled={pending || !onAsk || composer.trim().length === 0} onClick={onAsk}>
                {pending ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                Ask
              </Button>
              <Button type="button" variant="outline" onClick={onCancelDraft}>
                Cancel
              </Button>
            </div>
          </div>
        ) : null}
        {selected && !draft ? (
          <div className="space-y-3 border-t border-border pt-4">
            {selected.quote ? (
              <p className="text-xs text-muted-foreground">Near: {selected.quote}</p>
            ) : null}
            <div className="max-h-64 space-y-2 overflow-y-auto">
              {selected.messages.map((message) => (
                <div key={message.id}>
                  <p className="text-xs font-medium text-foreground">
                    {message.authorName}{' '}
                    <span className="font-normal text-muted-foreground">
                      {new Date(message.createdAt).toLocaleString()}
                    </span>
                  </p>
                  <p className="text-sm text-foreground">{message.body}</p>
                </div>
              ))}
            </div>
            <Field label="Reply" htmlFor="question-reply">
              <Textarea
                id="question-reply"
                value={composer}
                onChange={(event) => onComposerChange(event.target.value)}
                rows={4}
                maxLength={ENVELOPE_LIMITS.maxQuestionChars}
              />
            </Field>
            <Button disabled={pending || composer.trim().length === 0} onClick={onReply}>
              {pending ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
              Reply
            </Button>
          </div>
        ) : null}
      </CardContent>
    </Card>
  )
}
